import {
  AditudePageContext,
  AditudeRuntimeConfig,
  AditudeSlotMapping,
  TudeApi,
} from "./aditudeTypes";
import { installMockTude } from "./mockTude";

export const ADITUDE_SCRIPT_ID = "aditude-cloud-wrapper";

/**
 * Bootstrap the wrapper command queue. Safe to call any number of times and
 * before the wrapper script has loaded — that is the point of the queue.
 * Returns undefined during SSR.
 */
export function ensureTude(): TudeApi | undefined {
  if (typeof window === "undefined") return undefined;
  window.tude = window.tude || { cmd: [] };
  return window.tude;
}

/** Mock is effective only when explicitly requested. */
export function isAditudeMockEffective(config?: AditudeRuntimeConfig): boolean {
  return !!config?.mockMode;
}

/**
 * Inject the per-publisher Aditude wrapper script exactly once per page
 * (dedupe guard), or install the local mock when mock mode is explicitly on.
 */
export function loadAditudeWrapper(config: AditudeRuntimeConfig = {}): void {
  const tude = ensureTude();
  if (!tude) {
    return;
  }

  if (isAditudeMockEffective(config)) {
    installMockTude(tude, config);
    return;
  }

  // A blank wrapperUrl is a config gap, not a request for a fake ad — fail to
  // NO AD rather than showing real users the mock creative. Placements stay
  // empty and collapse if the slot is configured to.
  if (!config.wrapperUrl) {
    console.error("[aditude] wrapperUrl missing — no wrapper loaded");
    return;
  }

  if (document.getElementById(ADITUDE_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = ADITUDE_SCRIPT_ID;
  script.async = true;
  script.src = config.wrapperUrl as string;
  script.onload = () =>
    (script.onerror = () => console.error("[aditude] wrapper failed to load:", script.src));
  document.head.appendChild(script);
}

/**
 * Set page-level targeting and the GAM ad unit path — once per page, BEFORE
 * any slot mappings (mirrors the verified ETG reference page).
 */
export function setAditudePageContext(context: AditudePageContext): void {
  const tude = ensureTude();
  if (!tude) return;
  tude.cmd.push(() => {
    if (context?.targeting) window.tude?.setPageTargeting?.(context.targeting);
    if (context?.adUnitPath) window.tude?.setAdUnitPath?.(context.adUnitPath);
  });
}

/**
 * Map a rendered placeholder div to an Aditude slot. Must run AFTER the div
 * is in the DOM (in React: from useEffect, never during render).
 */
export function queueAditudeSlot(mapping: AditudeSlotMapping): void {
  const tude = ensureTude();
  if (!tude) return;
  tude.cmd.push(() => {
    window.tude?.refreshAdsViaDivMappings?.([mapping]);
  });
}

export interface GptSlotInfo {
  adUnitPath: string;
  divId: string;
}

/** Debug helper: list GPT slots so ad unit paths can be verified (real mode). */
export function getGptSlotInfo(): GptSlotInfo[] {
  if (typeof window === "undefined") return [];
  const gt = (window as any).googletag;
  if (!gt?.apiReady) return [];
  return gt
    .pubads()
    .getSlots()
    .map((slot: any) => ({
      adUnitPath: slot.getAdUnitPath(),
      divId: slot.getSlotElementId(),
    }));
}
